import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';

interface FormStateContextValue {
  touchedFields: Set<string>;
  markFieldAsTouched: (fieldName: string) => void;
  isFieldTouched: (fieldName: string) => boolean;
  hasAttemptedSubmit: boolean;
  markSubmitAttempted: () => void;
  validationErrors: Record<string, string>;
  setValidationErrors: (errors: Record<string, string>) => void;
  clearValidationError: (fieldName: string) => void;
}

const FormStateContext = createContext<FormStateContextValue | null>(null);

export interface FormStateProviderProps {
  children: React.ReactNode;
}

export const FormStateProvider: React.FC<FormStateProviderProps> = ({
  children,
}) => {
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Usar ref para evitar re-renders desnecessários ao marcar campos como touched
  const touchedFieldsRef = useRef<Set<string>>(touchedFields);
  touchedFieldsRef.current = touchedFields;

  const markFieldAsTouched = useCallback((fieldName: string) => {
    // Verificar se já está marcado para evitar re-renders desnecessários
    if (touchedFieldsRef.current.has(fieldName)) {
      return;
    }
    setTouchedFields((prev) => {
      const newSet = new Set(prev);
      newSet.add(fieldName);
      return newSet;
    });
  }, []);

  const isFieldTouched = useCallback(
    (fieldName: string) => {
      return touchedFields.has(fieldName);
    },
    [touchedFields]
  );

  const markSubmitAttempted = useCallback(() => {
    setHasAttemptedSubmit(true);
  }, []);

  const clearValidationError = useCallback((fieldName: string) => {
    setValidationErrors((prev) => {
      // Verificar se existe o erro antes de atualizar para evitar re-renders desnecessários
      if (!(fieldName in prev)) {
        return prev;
      }
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  const value = useMemo<FormStateContextValue>(
    () => ({
      touchedFields,
      markFieldAsTouched,
      isFieldTouched,
      hasAttemptedSubmit,
      markSubmitAttempted,
      validationErrors,
      setValidationErrors,
      clearValidationError,
    }),
    [
      touchedFields,
      markFieldAsTouched,
      isFieldTouched,
      hasAttemptedSubmit,
      markSubmitAttempted,
      validationErrors,
      clearValidationError,
    ]
  );

  return (
    <FormStateContext.Provider value={value}>
      {children}
    </FormStateContext.Provider>
  );
};

export const useFormState = (): FormStateContextValue => {
  const context = useContext(FormStateContext);
  if (!context) {
    throw new Error('useFormState deve ser usado dentro de FormStateProvider');
  }
  return context;
};

export default FormStateContext;
